import React, { useEffect, useMemo, useRef, useState } from 'react';
import useUserLocation from '../../hooks/useUserLocation';
import { closeLocationPanel } from '../../services/userLocation';
import { searchCities } from '../../utils/reverseGeocode';
import { getWallCache } from '../../utils/wallCache';
import portalService from '../../services/portalService';

/**
 * The location control (top-header handoff, Oct 14 2026) — the city the app shows events near.
 *
 * A pill in the header (or a button inside the FILTERS tab where the row has no room) and the
 * panel it opens. **Nothing here ever moves the city on its own**: the list, the recents, Enter
 * in the search field and USE MY CURRENT LOCATION are the only ways it changes, and the last of
 * those is the only thing in the app that reads the device's position.
 *
 * The towns offered are the ones the app already knows — the wall's own city pool first (the
 * towns you are actually being shown events in), then the shared city catalog — and anything
 * typed is searched properly, so the list is never a hard-coded one to maintain.
 */
export const PinGlyph = ({ size = 16, dot = '#fff' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden style={{ flexShrink: 0 }}>
    <path d="M12 22s-7.5-6.6-7.5-12.2A7.5 7.5 0 0 1 19.5 9.8C19.5 15.4 12 22 12 22z" fill="#FF00AA" />
    <circle cx="12" cy="9.8" r="2.8" fill={dot} />
  </svg>
);

/** The header pill: white at rest, the ring on black while hovered or open. */
export function LocationPill({ city, open, onToggle, size = 40, compact = false }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={`Location: ${city}. Change location`}
      aria-expanded={open}
      aria-haspopup="dialog"
      title="Change location"
      data-testid="location-pill"
      data-on={open ? 'true' : 'false'}
      className="th-loc font-mono"
      style={{ height: size, minWidth: size, padding: compact ? '0 12px 0 10px' : '0 14px 0 12px', fontSize: compact ? 11 : 12 }}
    >
      <PinGlyph size={16} dot={open ? '#000' : '#fff'} />
      <span className="truncate" style={{ minWidth: 0, maxWidth: compact ? 104 : 150 }}>{(city || '').toUpperCase()}</span>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden>
        <path d={open ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
      </svg>
    </button>
  );
}

/** The same control inside the FILTERS tab, where the header row has no room for the pill. */
export function LocationTabButton({ city, open, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={`Location: ${city}. Change location`}
      aria-expanded={open}
      aria-haspopup="dialog"
      data-testid="location-tab-button"
      className="flex items-center font-mono"
      style={{ gap: 6, height: '100%', padding: '0 12px 0 14px', border: 0, borderRight: '1px solid #333', background: 'none', color: '#fff', fontSize: 10, fontWeight: 800, letterSpacing: '.16em', cursor: 'pointer' }}
    >
      <PinGlyph size={12} dot="#000" />
      {(city || '').toUpperCase()}
    </button>
  );
}

// The shared city catalog, fetched once per load and kept in module scope: the panel opens
// with the towns the wall already pulled, and the catalog fills in the rest.
let catalogCache = null;
async function catalog() {
  if (catalogCache) return catalogCache;
  const r = await portalService.listCities();
  catalogCache = r?.success ? (r.data || []) : [];
  return catalogCache;
}

/** Towns the app already knows about, nearest-known first: the wall's pool, then the catalog. */
function knownTowns(catalogRows) {
  const out = [];
  const seen = new Set();
  const add = (name, lat = null, lng = null) => {
    const key = (name || '').trim().toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push({ name: name.trim(), lat, lng });
  };
  const cache = getWallCache();
  const pool = [...(cache?.snapshot?.allEvents || []), ...(cache?.snapshot?.featured || []), ...(cache?.events || [])];
  for (const e of pool) add((e.locationCity || '').split(',')[0].trim());
  for (const c of catalogRows || []) add(c.label, c.lat, c.lng);
  return out;
}

/**
 * The panel. Positioned against the header's bottom edge; the caller renders it inside the
 * header so `top: 100%` means "under the bar".
 */
export function LocationPanel({ phone = false, fallbackCity = '' }) {
  const loc = useUserLocation({ fallbackCity });
  const [q, setQ] = useState('');
  const [matches, setMatches] = useState([]);
  const [rows, setRows] = useState(() => knownTowns(catalogCache));
  const inputRef = useRef(null);
  const panelRef = useRef(null);

  // Autofocus 60ms after open: on a phone the panel is still sliding in, and iOS drops a focus
  // taken mid-transition.
  useEffect(() => { const t = setTimeout(() => inputRef.current?.focus(), 60); return () => clearTimeout(t); }, []);
  useEffect(() => { let done = false; catalog().then((c) => { if (!done) setRows(knownTowns(c)); }); return () => { done = true; }; }, []);

  // Esc closes, and so does a click anywhere outside it (the scrim covers the page below).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeLocationPanel(); };
    const onDown = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) closeLocationPanel(); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, []);

  const query = q.trim();
  useEffect(() => {
    if (!query) { setMatches([]); return undefined; }
    // What is already known matches instantly; the geocoder fills in anything else.
    const local = rows.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()));
    setMatches(local);
    const t = setTimeout(async () => {
      const found = await searchCities(query);
      setMatches((prev) => {
        const seen = new Set(prev.map((c) => c.name.toLowerCase()));
        return [...prev, ...found.filter((c) => !seen.has(c.name.toLowerCase()))];
      });
    }, 220);
    return () => clearTimeout(t);
  }, [query, rows]);

  const list = query ? matches : rows;
  const pick = (c) => {
    loc.setCity(c.name, typeof c.lat === 'number' ? { lat: c.lat, lng: c.lng } : null);
    closeLocationPanel();
  };

  const label = { fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: '#8A8A8A' };
  const recents = loc.recentsLabelled;
  const showRecent = !query && recents.length > 0;
  const cityMax = useMemo(() => (phone ? '30vh' : 240), [phone]);

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Change location"
      data-testid="location-panel"
      className="th-loc-panel font-mono"
      style={{
        position: 'absolute', zIndex: 6,
        top: phone ? 'calc(100% + 36px)' : 'calc(100% + 10px)',
        left: phone ? 12 : 28,
        width: phone ? 'calc(100% - 24px)' : 360,
        boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 16, padding: 18,
        border: '2px solid transparent', borderRadius: 24,
        background: 'linear-gradient(#000,#000) padding-box, var(--th-ring) border-box',
        color: '#fff',
      }}
    >
      <div className="flex items-start justify-between" style={{ gap: 12 }}>
        <div className="flex min-w-0 flex-col" style={{ gap: 6 }}>
          <span style={label}>SHOWING EVENTS NEAR</span>
          <span className="flex items-center truncate" style={{ gap: 8, fontSize: 18, fontWeight: 800, letterSpacing: '.1em' }}>
            <PinGlyph size={18} dot="#000" />
            {(loc.city || '').toUpperCase()}
          </span>
        </div>
        <button type="button" onClick={closeLocationPanel} aria-label="Close" className="flex flex-shrink-0 items-center justify-center" style={{ width: 32, height: 32, border: 0, borderRadius: 999, background: '#1F1F1F', color: '#fff', cursor: 'pointer' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="square"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      </div>

      <button
        type="button"
        onClick={loc.useCurrentLocation}
        disabled={loc.locating}
        data-testid="use-current-location"
        className="th-loc-here flex items-center justify-center font-mono"
        style={{ gap: 10, height: 44, padding: '0 16px', border: 0, borderRadius: 999, fontSize: 11, fontWeight: 800, letterSpacing: '.14em', cursor: 'pointer' }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden>
          <circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="1.5" fill="currentColor" /><path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
        </svg>
        {loc.locating ? 'LOCATING…' : 'USE MY CURRENT LOCATION'}
      </button>
      {loc.error && <span style={{ fontSize: 11, lineHeight: 1.5, color: '#FF00AA', marginTop: -10 }}>{loc.error}</span>}

      <label className="flex items-center" style={{ gap: 10, height: 44, padding: '0 8px 0 16px', borderRadius: 999, background: '#161616' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" strokeWidth="2.5" strokeLinecap="square" aria-hidden style={{ flexShrink: 0 }}><circle cx="11" cy="11" r="7" /><path d="M21 21l-5-5" /></svg>
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); if (list[0]) pick(list[0]); }
            if (e.key === 'Escape') { e.stopPropagation(); closeLocationPanel(); }
          }}
          placeholder="SEARCH A CITY OR TOWN"
          aria-label="Search a city or town"
          data-testid="location-search"
          className="th-input"
          style={{ fontSize: phone ? 16 : 14 }}
        />
      </label>

      {showRecent && (
        <div className="flex flex-col" style={{ gap: 4 }}>
          <div className="flex items-baseline justify-between" style={{ padding: '0 4px 6px' }}>
            <span style={label}>RECENT LOCATIONS</span>
            <button type="button" onClick={loc.clearRecents} className="font-mono" style={{ border: 0, background: 'none', padding: 0, color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: '.1em', cursor: 'pointer' }}>CLEAR</button>
          </div>
          {recents.map((r) => (
            <button key={r.name} type="button" onClick={() => pick({ name: r.name })} className="th-loc-row font-mono" data-testid="recent-location">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" strokeWidth="2.5" strokeLinecap="round" aria-hidden><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
              <span className="truncate">{r.name.toUpperCase()}</span>
              <span style={{ fontSize: 10, color: '#8A8A8A' }}>{r.when}</span>
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-col" style={{ gap: 4 }}>
        <span style={{ ...label, padding: '0 4px 6px' }}>{query ? 'MATCHES' : 'NEARBY'}</span>
        <div className="th-scroll flex flex-col" style={{ gap: 2, maxHeight: cityMax, overflowY: 'auto' }}>
          {list.map((c) => {
            const current = c.name.toLowerCase() === (loc.city || '').toLowerCase();
            return (
              <button
                key={`${c.name}:${c.lat ?? ''}`}
                type="button"
                onClick={() => pick(c)}
                aria-current={current || undefined}
                data-on={current ? 'true' : 'false'}
                className="th-loc-city font-mono"
              >
                <span className="truncate">{c.name.toUpperCase()}{c.region ? <span style={{ color: current ? '#6B7280' : '#8A8A8A' }}>{` · ${c.region.toUpperCase()}`}</span> : ''}</span>
                {current && <span style={{ fontSize: 10, fontWeight: 700, color: '#FF00AA' }}>CURRENT</span>}
              </button>
            );
          })}
          {!list.length && (
            <span style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF' }}>
              {query ? `No towns match “${query}”.` : 'Search for the town you want.'}
            </span>
          )}
        </div>
      </div>

      <span style={{ fontSize: 11, lineHeight: 1.5, color: '#9CA3AF', padding: '0 4px' }}>Your location stays set until you change it here.</span>
    </div>
  );
}
